import { Img, Layout, Rect, Txt, brightness, saturate, type Node } from "@revideo/2d";
import { isRtl, type Clip } from "@hudhud/core";
import { COLORS, END_BG, END_FLAT, HEIGHT, WIDTH, buttonPulse, endEnter, rgba, typeHandleSize } from "@hudhud/reel/math";
import { circleGradient, verticalGradient } from "./gradients";
import { family, isRtlText, paragraph, runsOf, weight } from "./text";
import { stillNode } from "./video";

export interface EndCardInput { clip: Clip; logoUrl: string; mediaUrl: string; mediaStartMs: number; frame: () => number; fps: number; lite?: boolean }

/** The card after the speech in its five styles, arriving with its animation. Mirrors EndCard in the Remotion reel. */
export function endCardNode(o: EndCardInput): Node {
  const { clip, frame, fps } = o;
  const e = clip.endCard;
  const rtl = isRtl(clip.captionLang);
  const light = e.bg === "gold" || e.bg === "white";
  const onStill = e.style === "poster";
  const text = onStill ? COLORS.offWhite : e.textColor;
  const accent = onStill && e.bg === "gold" ? COLORS.gold : e.accentColor;
  const scale = e.fontSize / 100;
  const font = family(e.font);
  const w = (n: number) => weight(e.font, n);
  const keep = (nodes: (Node | null)[]) => nodes.filter((n): n is Node => n !== null);
  const enter = () => endEnter(e.animation, frame(), fps);

  const button = e.button && e.buttonText.trim() ? new Rect({
    layout: true, direction: "row", alignItems: "center", gap: 14, padding: [20 * scale, 46 * scale], radius: 999, fill: accent,
    marginTop: 44 * scale, shadowColor: "rgba(0,0,0,0.35)", shadowBlur: 50, shadowOffset: [0, 18],
    scale: () => buttonPulse(frame()), opacity: () => enter().enter,
    children: [
      new Txt({ text: e.buttonText, fontFamily: font, fontWeight: w(800), fontSize: 40 * scale, letterSpacing: -0.5, fill: light && !onStill ? COLORS.offWhite : "#1a1300" }),
      new Txt({ text: "›", fontFamily: font, fontWeight: w(800), fontSize: 44 * scale, lineHeight: "80%", fill: light && !onStill ? COLORS.offWhite : "#1a1300" }),
    ],
  }) : null;
  const textWidth = WIDTH - 160;
  const sub = e.sub.trim() ? paragraph({ runs: [{ text: e.sub }], font: e.font, weight: 500, size: 34 * scale, maxWidth: textWidth, fill: rgba(text, 0.75), align: "center", lineHeight: rtl ? 1.5 : 1.2, rtl: rtl || isRtlText(e.sub), preLine: true, block: { marginTop: 18 * scale } }) : null;
  const line = paragraph({ runs: runsOf(e.line), font: e.font, weight: 600, size: 40 * scale, maxWidth: textWidth, fill: rgba(text, 0.95), accentFill: accent, align: "center", lineHeight: rtl ? 1.5 : 1.2, letterSpacing: rtl ? 0 : -0.5, rtl: rtl || isRtlText(e.line), preLine: true, block: { marginTop: 18 * scale } });
  const logo = (width: number) => (e.showLogo ? new Img({ src: o.logoUrl, width: width * e.logoScale, ...(light && !onStill ? {} : { shadowColor: "rgba(0,0,0,0.5)", shadowBlur: 30, shadowOffset: [0, 12] }) }) : null);
  const nudge = (children: (Node | null)[]) => new Layout({ layout: true, direction: "column", alignItems: "center", position: [e.offsetX, e.offsetY], children: children.filter((n): n is Node => !!n) });
  /** the centred column every style lays its text in, carrying the entrance */
  const column = (children: (Node | null)[], extra: Partial<{ justifyContent: "center" | "end"; paddingBottom: number }> = {}) => new Layout({
    layout: true, direction: "column", alignItems: "center", justifyContent: extra.justifyContent ?? "center", width: WIDTH, height: HEIGHT,
    padding: [0, 80, extra.paddingBottom ?? 0, 80],
    opacity: () => enter().opacity ?? 1, y: () => enter().ty ?? 0, scale: () => enter().scale ?? 1,
    children: children.filter((n): n is Node => !!n),
  });
  const bgRect = (kind: "gradient" | "flat") => new Rect({ width: WIDTH, height: HEIGHT, fill: kind === "flat" ? END_FLAT[e.bg] : circleGradient(WIDTH, HEIGHT, END_BG[e.bg].cx, END_BG[e.bg].cy, END_BG[e.bg].stops) });
  const still = (extra: Partial<ConstructorParameters<typeof Layout>[0]>) => new Layout({ width: WIDTH, height: HEIGHT, ...extra, children: [stillNode({ clip, mediaUrl: o.mediaUrl, mediaStartMs: o.mediaStartMs, videoMs: clip.coverMs ?? 1000, lite: o.lite })] });

  if (e.style === "type") {
    const size = typeHandleSize(e.handle, scale);
    return new Layout({ width: WIDTH, height: HEIGHT, children: [
      bgRect("gradient"),
      column([nudge([
        paragraph({ runs: runsOf(e.line), font: e.font, weight: 600, size: 38 * scale, maxWidth: textWidth, fill: rgba(text, 0.85), accentFill: accent, align: "center", lineHeight: 1.2, letterSpacing: rtl ? 0 : 6, rtl: rtl || isRtlText(e.line), uppercase: !rtl }),
        new Txt({ text: e.handle, marginTop: 30, fontFamily: font, fontWeight: w(900), fontSize: size, lineHeight: "95%", letterSpacing: -size * 0.04, fill: accent, ...(light ? {} : { shadowColor: "rgba(0,0,0,0.35)", shadowBlur: 60, shadowOffset: [0, 20] }) }),
        sub, button,
      ])]),
      new Layout({ layout: true, justifyContent: "center", width: WIDTH, offset: [0, 1], y: HEIGHT - 150 - HEIGHT / 2, opacity: () => enter().opacity ?? 1, children: keep([logo(300)]) }),
    ] });
  }
  if (e.style === "minimal") {
    return new Layout({ width: WIDTH, height: HEIGHT, children: [
      bgRect("flat"),
      column([logo(360), new Rect({ width: 90, height: 6, radius: 3, fill: accent, marginTop: 50 * scale, marginBottom: 40 * scale }),
        nudge([new Txt({ text: e.handle, fontFamily: font, fontWeight: w(800), fontSize: 64 * scale, fill: text, letterSpacing: -1 }), line, sub, button])]),
    ] });
  }
  if (e.style === "poster") {
    return new Layout({ width: WIDTH, height: HEIGHT, children: [
      new Rect({ width: WIDTH, height: HEIGHT, fill: COLORS.ink }),
      still({ filters: [brightness(0.55), saturate(1.05)], scale: () => 1.04 + 0.03 * enter().enter }),
      new Rect({ width: WIDTH, height: HEIGHT, fill: verticalGradient(HEIGHT, "to top", [{ offset: 0, color: "rgba(11,15,13,0.92)" }, { offset: 0.4, color: "rgba(11,15,13,0.55)" }, { offset: 1, color: "rgba(11,15,13,0.15)" }]) }),
      column([logo(320), nudge([new Txt({ text: e.handle, marginTop: 40 * scale, fontFamily: font, fontWeight: w(900), fontSize: 96 * scale, fill: accent, letterSpacing: -2, shadowColor: "rgba(0,0,0,0.6)", shadowBlur: 40, shadowOffset: [0, 10] }), line, sub, button])], { justifyContent: "end", paddingBottom: 260 }),
    ] });
  }
  if (e.style === "split") {
    const top = HEIGHT * 0.5;
    return new Layout({ width: WIDTH, height: HEIGHT, children: [
      bgRect("flat"),
      new Rect({ width: WIDTH, height: top, y: top / 2 - HEIGHT / 2, clip: true, radius: [0, 0, 60, 60], children: [still({ filters: [brightness(0.8)], y: HEIGHT / 2 - top / 2 })] }),
      new Layout({
        layout: true, direction: "column", alignItems: "center", justifyContent: "center", width: WIDTH, height: HEIGHT - top, y: top / 2, padding: [0, 80],
        opacity: () => enter().opacity ?? 1, scale: () => enter().scale ?? 1,
        children: keep([logo(300), nudge([new Txt({ text: e.handle, marginTop: 30 * scale, fontFamily: font, fontWeight: w(900), fontSize: 84 * scale, fill: accent, letterSpacing: -1.5 }), line, sub, button])]),
      }),
    ] });
  }
  // classic
  return new Layout({ width: WIDTH, height: HEIGHT, children: [
    bgRect("gradient"),
    column([logo(720), nudge([new Txt({ text: e.handle, marginTop: 70 * scale, fontFamily: font, fontWeight: w(800), fontSize: 66 * scale, fill: accent, letterSpacing: -1 }), line, sub, button])]),
  ] });
}
